import React, { useState } from 'react';
import { SEOHead } from '../components/common/SEOHead';
import { Breadcrumbs } from '../components/common/Breadcrumbs';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { createCatalogDownload } from '../lib/api';
import { toast } from 'sonner';
import { Download, FileText, CheckCircle } from 'lucide-react';

// Catalog PDF URL
const CATALOG_PDF_URL = 'https://customer-assets.emergentagent.com/job_bf699e35-1a4e-40f4-b3f7-f0130b34dab2/artifacts/x2p8ecyl_Passion%20of%20Aroma%20Product%20Catalog.pdf';

export default function DownloadCatalogPage() {
  const [loading, setLoading] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const triggerDownload = () => {
    // Create a temporary link and trigger download
    const link = document.createElement('a');
    link.href = CATALOG_PDF_URL;
    link.download = 'Passion_of_Aroma_Product_Catalog.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await createCatalogDownload({
        ...formData,
        source_page_url: window.location.href,
      });

      // Track conversion event
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'catalog_download', {
          source_page: '/download-catalog',
        });
      }

      toast.success('Thank you! Your catalog download is starting...');
      
      // Trigger the PDF download
      triggerDownload();
      setDownloaded(true);
      
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
      console.error('Catalog download error:', error);
    } finally {
      setLoading(false);
    }
  };

  const catalogFeatures = [
    'Complete product listing with images',
    '50+ international brands',
    '1000+ products across 6 categories',
    'MOQ and availability information',
  ];

  return (
    <>
      <SEOHead
        title="Download Product Catalog - Wholesale Fragrance Catalog"
        description="Download our complete wholesale fragrance catalog. Get detailed product listings, brand information, and wholesale pricing for 1000+ products."
        canonical="https://passionofaroma.com/download-catalog"
      />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-[#0B1F3B]" data-testid="catalog-hero">
        <div className="container-custom">
          <Breadcrumbs items={[{ label: 'Download Catalog' }]} />
          <div className="mt-6 max-w-2xl">
            <span className="label-text mb-4 block">Free Download</span>
            <h1 className="heading-xl text-white mb-4">Product Catalog</h1>
            <p className="text-gray-300 text-lg">
              Get our complete product catalog with detailed information on 1000+ 
              premium fragrances and cosmetics.
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-[#F7F6F3]" data-testid="catalog-content">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Catalog Info */}
            <div>
              <div className="bg-white p-8 mb-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-[#0B1F3B] flex items-center justify-center">
                    <FileText className="h-8 w-8 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h2 className="heading-md">Passion of Aroma</h2>
                    <p className="text-gray-500">Wholesale Product Catalog 2024</p>
                  </div>
                </div>
                <div className="gold-separator mb-6" />
                <h3 className="font-medium text-[#0B1F3B] mb-4">What's Included:</h3>
                <ul className="space-y-3">
                  {catalogFeatures.map((feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-[#D4AF37]" />
                      <span className="text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Categories Preview */}
              <div className="bg-white p-8">
                <h3 className="font-medium text-[#0B1F3B] mb-4">Categories Covered:</h3>
                <div className="grid grid-cols-2 gap-3">
                  {['Perfume', 'Deodorant', 'Body Mist', 'Gift Set', 'Attar', 'Cosmetics'].map((cat) => (
                    <div key={cat} className="p-3 bg-[#F7F6F3] text-center text-sm">
                      {cat}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Download Form */}
            <div className="bg-white p-8 lg:p-12 sticky top-24">
              {!downloaded ? (
                <>
                  <div className="text-center mb-8">
                    <Download className="h-12 w-12 text-[#D4AF37] mx-auto mb-4" />
                    <h2 className="heading-md mb-2">Get Your Free Catalog</h2>
                    <p className="text-gray-500">
                      Fill in your details to download the complete catalog
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-5" data-testid="catalog-form">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="label-text">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="input-luxury"
                        placeholder="Your name"
                        data-testid="catalog-name"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="company" className="label-text">Company Name *</Label>
                      <Input
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        required
                        className="input-luxury"
                        placeholder="Your company"
                        data-testid="catalog-company"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email" className="label-text">Business Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="input-luxury"
                        placeholder="you@company.com"
                        data-testid="catalog-email"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone" className="label-text">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="input-luxury"
                        placeholder="+91 98765 43210"
                        data-testid="catalog-phone"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={loading}
                      className="btn-primary w-full"
                      data-testid="catalog-submit"
                    >
                      {loading ? 'Processing...' : 'Download Catalog'}
                    </Button>

                    <p className="text-xs text-gray-400 text-center">
                      By downloading, you agree to our Privacy Policy and consent to 
                      receive business communications.
                    </p>
                  </form>
                </>
              ) : (
                <div className="text-center py-8" data-testid="download-success">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="h-10 w-10 text-green-600" />
                  </div>
                  <h2 className="heading-md mb-4">Download Started!</h2>
                  <p className="text-gray-500 mb-6">
                    Thank you, {formData.name}! Your catalog download should begin automatically.
                  </p>
                  <p className="text-sm text-gray-400 mb-6">
                    If the download doesn't start, click the button below:
                  </p>
                  <Button 
                    onClick={triggerDownload}
                    className="btn-primary"
                    data-testid="download-again"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download Again
                  </Button>
                  <div className="mt-8 pt-6 border-t border-gray-100">
                    <p className="text-sm text-gray-500 mb-4">
                      Our team will contact you shortly to discuss your requirements.
                    </p>
                    <a 
                      href="/contact" 
                      className="text-[#D4AF37] hover:underline text-sm font-medium"
                    >
                      Contact us directly →
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
